import {METHOD_DELETE, METHOD_GET, METHOD_POST, METHOD_PUT} from '@constants/http';
import type {
    ChangeEmailParams,
    ChangeEmailResponseData,
    ChangePasswordFormInputs,
    ForgotPasswordFormInputs,
    GetUserDetailsContext,
    GetUserListContext,
    GetUserListResponseData,
    MeResponseData,
    ResetPasswordParams,
    UserDetails,
} from '@interfaces/user';
import type {Payload} from '@interfaces/api';
import AuthRequest from '@api/request/AuthRequest';
import JsonRequest from '@api/request/JsonRequest';
import type {QueryFunctionContext} from '@tanstack/react-query';

export async function ping(): Promise<Payload> {
    return new AuthRequest(new JsonRequest('/api/user/ping', METHOD_GET)).send();
}

export async function me(): Promise<Payload<MeResponseData>> {
    return new AuthRequest(new JsonRequest('/api/user/me', METHOD_GET)).send();
}

export async function verifyUser(code: string): Promise<Payload> {
    return new AuthRequest(new JsonRequest(`/api/user/verify/${code}`, METHOD_POST)).send();
}

export async function sendVerificationCode(): Promise<Payload> {
    return new AuthRequest(new JsonRequest('/api/user/verify/code', METHOD_POST)).send();
}

export async function sendPasswordResetCode(values: ForgotPasswordFormInputs): Promise<Payload> {
    return new JsonRequest('/api/user/resetpass/code', METHOD_POST).setParams(values).send();
}

export async function resetPassword({code, formInputs}: ResetPasswordParams): Promise<Payload> {
    return new JsonRequest(`/api/user/resetpass/${code}`, METHOD_POST).setParams(formInputs).send();
}

export async function changePassword(values: ChangePasswordFormInputs): Promise<Payload> {
    return new AuthRequest(
        new JsonRequest('/api/user/password', METHOD_POST).setParams(values),
    ).send();
}

export async function deleteMe(): Promise<Payload> {
    return new AuthRequest(new JsonRequest('/api/user/me', METHOD_DELETE)).send();
}

export async function setOnboarded(): Promise<Payload> {
    return new AuthRequest(new JsonRequest('/api/user/onboarded', METHOD_POST)).send();
}

export async function getUserList({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    queryKey: [_key, {search, page}],
}: QueryFunctionContext<GetUserListContext>): Promise<Payload<GetUserListResponseData>> {
    return new AuthRequest(
        new JsonRequest(
            `/api/user/admin/list?page=${page}&search=${encodeURIComponent(search)}`,
            METHOD_GET,
        ),
    ).send();
}

export async function getUserDetails({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    queryKey: [_key, {id}],
}: QueryFunctionContext<GetUserDetailsContext>): Promise<Payload<UserDetails>> {
    return new AuthRequest(new JsonRequest(`/api/user/admin/${id}`, METHOD_GET)).send();
}

export async function changeEmail(
    params: ChangeEmailParams,
): Promise<Payload<ChangeEmailResponseData>> {
    return new AuthRequest(
        new JsonRequest(`/api/user/admin/email`, METHOD_PUT).setParams(params),
    ).send();
}
